import { defineStore } from 'pinia'

const MAX_PINNED = 30
const MAX_DISMISSED_HISTORY = 200
const SELF_PIN_DEDUPE_WINDOW_MS = 2000
const PANEL_MIN_W = 295
const PANEL_MIN_H = 170

export interface PinnedShareItem {
  modelName: string
  modelId: string
  kind?: 'source' | 'linked'
  depth?: number
  parentModelName?: string
  parentModelId?: string
}

export interface PinnedShare {
  id: string
  notificationId?: string
  items: PinnedShareItem[]
  senderId?: string
  senderName?: string
  note?: string | null
  sharedAt?: string | null
  pinnedAt: number
}

interface State {
  pinned: PinnedShare[]
  panelPosition: { x: number; y: number } | null
  panelSize: { w: number; h: number } | null
  collapsed: boolean
  hidden: boolean
  docked: boolean
  collapsedShareIds: string[]
  dismissedNotificationIds: string[]
}

function itemsSignature(items: PinnedShareItem[]): string {
  return items.map((i) => `${i.modelName}|${i.modelId}`).sort().join(';')
}

interface PinInput {
  id?: string
  notificationId?: string
  items?: PinnedShareItem[]
  // Legacy single-model shape — accepted on the way in and normalized into `items`
  modelName?: string
  modelIds?: (string | number)[]
  senderId?: string
  senderName?: string
  note?: string | null
  sharedAt?: string | null
}

function normalizeItems(input: PinInput): PinnedShareItem[] {
  if (input.items?.length) {
    return input.items.map((item) => ({
      modelName: item.modelName,
      modelId: String(item.modelId),
      kind: item.kind ?? 'source',
      depth: item.depth ?? (item.kind === 'linked' ? 1 : 0),
      parentModelName: item.parentModelName,
      parentModelId: item.parentModelId ? String(item.parentModelId) : undefined,
    }))
  }
  if (input.modelName && input.modelIds?.length) {
    return input.modelIds.map((id) => ({ modelName: input.modelName!, modelId: String(id), kind: 'source', depth: 0 }))
  }
  return []
}

export const usePinnedSharesStore = defineStore('pinned-shares', {
  state: (): State => ({
    pinned: [],
    panelPosition: null,
    panelSize: null,
    collapsed: false,
    hidden: false,
    docked: false,
    collapsedShareIds: [],
    dismissedNotificationIds: [],
  }),
  getters: {
    hasPinned: (state) => state.pinned.length > 0,
    count: (state) => state.pinned.length,
  },
  actions: {
    pin(input: PinInput) {
      const items = normalizeItems(input)
      if (items.length === 0) return

      // Echo replays during reconnect re-fire `created` events for shares the
      // user has already explicitly dismissed. Skip those so an unpinned share
      // doesn't silently reappear on every reconnect.
      if (input.notificationId && this.dismissedNotificationIds.includes(input.notificationId)) {
        return
      }

      // Surface the panel even when the share is already pinned. Without this,
      // re-pinning a known share (e.g. clicking it again in the
      // NotificationCenter) hits the dedupe path and the panel stays hidden.
      this.hidden = false

      const id = input.id ?? `${input.notificationId ?? Date.now()}-${Math.random().toString(36).slice(2, 8)}`
      if (this.pinned.some((p) => p.id === id || (p.notificationId && p.notificationId === input.notificationId))) {
        return
      }

      // Self-pin double-click guard: collapse same-content pins fired within
      // a short window onto the existing entry. Notification-backed pins
      // already dedupe via `notificationId` above.
      if (!input.notificationId) {
        const sig = itemsSignature(items)
        const now = Date.now()
        const recent = this.pinned[this.pinned.length - 1]
        if (
          recent
          && !recent.notificationId
          && now - recent.pinnedAt < SELF_PIN_DEDUPE_WINDOW_MS
          && itemsSignature(recent.items) === sig
        ) {
          return
        }
      }

      this.pinned.push({
        id,
        notificationId: input.notificationId,
        items,
        senderId: input.senderId,
        senderName: input.senderName,
        note: input.note ?? null,
        sharedAt: input.sharedAt ?? null,
        pinnedAt: Date.now(),
      })

      // FIFO cap so localStorage and the rendered q-slide-item list don't grow
      // unbounded across long sessions.
      if (this.pinned.length > MAX_PINNED) {
        const drop = this.pinned.length - MAX_PINNED
        const dropped = this.pinned.splice(0, drop)
        const droppedIds = new Set(dropped.map((d) => d.id))
        this.collapsedShareIds = this.collapsedShareIds.filter((cid) => !droppedIds.has(cid))
      }
    },
    /**
     * Pin from a notification payload (snake_case shape from the backend).
     * Handles both the new multi-model `items[]` shape and the legacy
     * `model_name` + `model_ids[]` shape.
     */
    pinFromNotification(notification: { id: string; created_at?: string | null; data?: Record<string, unknown> }) {
      const data = notification.data
      if (!data || data.type !== 'crud_model_shared') return

      const rawItems = Array.isArray(data.items) ? (data.items as Record<string, unknown>[]) : []
      const validItems = rawItems.filter((item) => item.model_name != null && item.model_id != null)
      const items: PinnedShareItem[] | undefined = validItems.length > 0
        ? validItems.map((item) => ({
            modelName: String(item.model_name),
            modelId: String(item.model_id),
            kind: (item.kind === 'linked' ? 'linked' : 'source') as 'source' | 'linked',
            depth: typeof item.depth === 'number' ? item.depth : 0,
            parentModelName: item.parent_model_name ? String(item.parent_model_name) : undefined,
            parentModelId: item.parent_model_id ? String(item.parent_model_id) : undefined,
          }))
        : undefined

      const legacyModelIds = Array.isArray(data.model_ids)
        ? (data.model_ids as (string | number)[])
        : data.model_id != null
          ? [data.model_id as string | number]
          : []

      this.pin({
        notificationId: notification.id,
        items,
        modelName: items ? undefined : (data.model_name as string | undefined),
        modelIds: items ? undefined : legacyModelIds,
        senderId: data.sender_id as string | undefined,
        senderName: data.sender_name as string | undefined,
        note: (data.note as string | null) ?? null,
        sharedAt: notification.created_at ?? null,
      })
    },
    unpin(id: string) {
      const removed = this.pinned.find((p) => p.id === id)
      this.pinned = this.pinned.filter((p) => p.id !== id)
      this.collapsedShareIds = this.collapsedShareIds.filter((cid) => cid !== id)
      // Remember dismissals so an echo replay on reconnect doesn't revive a
      // share the user already cleared. Capped FIFO so the persisted blob
      // doesn't grow unbounded across long-lived sessions.
      if (removed?.notificationId && !this.dismissedNotificationIds.includes(removed.notificationId)) {
        this.dismissedNotificationIds.push(removed.notificationId)
        if (this.dismissedNotificationIds.length > MAX_DISMISSED_HISTORY) {
          this.dismissedNotificationIds.splice(0, this.dismissedNotificationIds.length - MAX_DISMISSED_HISTORY)
        }
      }
    },
    clear() {
      this.pinned = []
      this.collapsedShareIds = []
      this.hidden = false
    },
    setPosition(x: number, y: number) {
      this.panelPosition = { x, y }
    },
    setSize(w: number, h: number) {
      this.panelSize = { w, h }
    },
    resetSize() {
      this.panelSize = null
    },
    /**
     * Pull a previously-persisted position/size back into the current viewport.
     * Switching from an external monitor to a smaller screen would otherwise
     * render the panel partially or fully off-screen.
     */
    clampToViewport(viewportWidth: number, viewportHeight: number) {
      if (this.panelSize) {
        const maxW = Math.max(PANEL_MIN_W, viewportWidth * 0.95)
        const maxH = Math.max(PANEL_MIN_H, viewportHeight * 0.9)
        const w = Math.max(PANEL_MIN_W, Math.min(maxW, this.panelSize.w))
        const h = Math.max(PANEL_MIN_H, Math.min(maxH, this.panelSize.h))
        if (w !== this.panelSize.w || h !== this.panelSize.h) {
          this.panelSize = { w, h }
        }
      }
      if (this.panelPosition) {
        const w = this.panelSize?.w ?? PANEL_MIN_W
        const maxX = Math.max(0, viewportWidth - Math.min(w, viewportWidth))
        const maxY = Math.max(0, viewportHeight - 60)
        const x = Math.max(0, Math.min(maxX, this.panelPosition.x))
        const y = Math.max(0, Math.min(maxY, this.panelPosition.y))
        if (x !== this.panelPosition.x || y !== this.panelPosition.y) {
          this.panelPosition = { x, y }
        }
      }
    },
    isShareCollapsed(id: string): boolean {
      return this.collapsedShareIds.includes(id)
    },
    toggleShareCollapsed(id: string) {
      if (this.collapsedShareIds.includes(id)) {
        this.collapsedShareIds = this.collapsedShareIds.filter((cid) => cid !== id)
      } else {
        this.collapsedShareIds = [...this.collapsedShareIds, id]
      }
    },
    toggleCollapsed() {
      this.collapsed = !this.collapsed
    },
    hide() {
      this.hidden = true
    },
    show() {
      this.hidden = false
    },
    setDocked(value: boolean) {
      this.docked = value
    },
    /**
     * Migrate legacy entries (modelName + modelIds) into the new items shape.
     * Called by the panel on mount so persisted state from earlier versions
     * keeps working without forcing the user to clear localStorage.
     */
    migrateLegacy() {
      // Idempotent: skip when every entry already carries a populated `items`
      // array (the new shape) so panel mounts after the migration don't
      // needlessly re-allocate every entry.
      if (this.pinned.every((entry) => entry.items?.length)) return
      this.pinned = this.pinned.map((entry) => {
        const items = entry.items?.length
          ? entry.items.map((item) => ({ ...item, kind: item.kind ?? 'source' as const }))
          : (() => {
              const legacy = entry as PinnedShare & { modelName?: string; modelIds?: (string | number)[] }
              return legacy.modelName && legacy.modelIds?.length
                ? legacy.modelIds.map((id) => ({ modelName: legacy.modelName!, modelId: String(id), kind: 'source' as const }))
                : []
            })()
        return { ...entry, items }
      })
    },
  },
  persist: true,
})
